/**
 * Company: SPHMedia
 * Description: [Gets Short Form videos data]
 */

import fetchBHApi from "@helper/fetchBHApi";
import {
  ENABLE_MEDIA_CONTENT_INFINITE_SCROLL,
  ENABLE_NEW_MULTIMEDIA_DESIGN,
  HOMEPAGE_PORTRAIT_VIDEO_SECTION,
} from "@helper/getEnvVariables";
import type { LatestVideoResponse } from "@typings/Brightcove.d";
import { PlaylistType } from "@typings/Playlist.d";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

type useShortFormVideoData = {
  playing: LatestVideoResponse | undefined;
  videos: LatestVideoResponse[];
  setVideos: Dispatch<SetStateAction<LatestVideoResponse[]>>;
  handleOnClickThumbnail: (props: LatestVideoResponse) => void;
  handleOnEndedVideo: () => void;
};

/**
 * Custom React Hook: useShortFormVideo
 * Description: [Gets Short Form videos from bc playlist]
 * @returns data - The data returned by the hook
 */
export default function useShortFormVideo({
  parentRef,
  limit = 6,
}: {
  parentRef?: React.RefObject<HTMLDivElement | null>;
  limit?: number;
}): useShortFormVideoData {
  const [videos, setVideos] = useState<LatestVideoResponse[]>([]);
  const [playing, setPlaying] = useState<LatestVideoResponse | undefined>();
  const hasInitialized = useRef(false);

  const handleNowPlaying = useMemo(
    () => (id: string) => {
      setVideos((prevValue) =>
        prevValue.map((video) => ({
          ...video,
          nowPlaying: video.id === id,
        })),
      );
    },
    [setVideos],
  );

  const handleOnClickThumbnail = useCallback(
    (props: LatestVideoResponse) => {
      const { id } = props;
      handleNowPlaying(id);
      setPlaying(props);
    },

    [handleNowPlaying, setPlaying],
  );

  const handleOnEndedVideo = useCallback(() => {
    const playlistLength = videos.length;
    if (playlistLength === 0) return;

    let index: number = videos.findIndex((video) => video.id === playing?.id);

    index++; // Move to the next video in the playlist
    handleNowPlaying(videos[index % playlistLength].id);
    setPlaying(videos[index % playlistLength]);
  }, [handleNowPlaying, videos, playing]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const videos: LatestVideoResponse[] = await fetchBHApi<
          LatestVideoResponse[]
        >(
          `latest-videos-playlist?playlistId=${PlaylistType.SHORT_FORM}&size=${limit}`,
          "GET",
        );
        const limitedContent = videos.slice(0, limit);
        setVideos(limitedContent);
      } catch (error) {
        console.error("Something went wrong", error);
      }
    };
    if (
      ENABLE_MEDIA_CONTENT_INFINITE_SCROLL ||
      HOMEPAGE_PORTRAIT_VIDEO_SECTION ||
      ENABLE_NEW_MULTIMEDIA_DESIGN
    ) {
      void fetchData();
    }
  }, [limit]);

  useEffect(() => {
    const element = parentRef?.current;

    if (!element || !videos.length || hasInitialized.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting &&
          entry.intersectionRatio >= 0.25 &&
          !hasInitialized.current
        ) {
          hasInitialized.current = true;

          setPlaying(videos[0]);
          handleNowPlaying(videos[0].id);

          observer.disconnect();
        }
      },
      {
        threshold: [0.25],
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [parentRef, videos, handleNowPlaying]);

  return {
    playing,
    videos,
    setVideos,
    handleOnClickThumbnail,
    handleOnEndedVideo,
  };
}
